import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowLeft, Home, CreditCard } from 'lucide-react';

// Main Payment Result Component that handles routing based on status
export const PaymentResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  console.log("status", status)
  // Route to appropriate component based on status
  if (status === 'success' || status === 'active' || status === 'completed') {
    return <PaymentSuccess />;
  } else if (status === 'failed') {
    return <PaymentFailure />;
  } else {
    // Handle cases where status is missing or invalid
    return <PaymentFailure />;
  }
};

// Payment Success Component
export const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subscriptionId = searchParams.get('subscription_id');
  const status = searchParams.get('status');
  const planName = searchParams.get('plan');

  useEffect(() => {
    // Verify payment and update user's plan
    if (status === 'success' && subscriptionId) {
      console.log('Payment successful:', { subscriptionId, planName });
      
      // Call your API to verify and activate the subscription
     // verifyAndActivateSubscription(subscriptionId);
    }
  }, [subscriptionId, status, planName]);

  const verifyAndActivateSubscription = async (subId: string) => {
    try {
      // Replace with your actual API endpoint
      const response = await fetch('/api/verify-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscription_id: subId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Subscription activated:', data);
      } else {
        console.error('Failed to verify subscription');
      }
    } catch (error) {
      console.error('Error verifying subscription:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          <p className="text-gray-600">
            Thank you for your payment. Your subscription has been activated successfully.
          </p>
        </div>

        {subscriptionId && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-green-800 mb-1">Subscription Activated</h3>
            <p className="text-green-700 mb-2">
              Your subscription has been successfully activated.
            </p>
            <p className="text-sm text-green-600">
              Subscription ID: {subscriptionId}
            </p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => navigate('/profile')}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            View Profile
          </button>
          <button
            onClick={() => navigate('/sites')}
            className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Go to Dashboard
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            You'll receive a confirmation email shortly with your receipt and subscription details.
          </p>
        </div>
      </div>
    </div>
  );
};

// Payment Failure Component
export const PaymentFailure: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subscriptionId = searchParams.get('subscription_id');
  const status = searchParams.get('status');
  const error = searchParams.get('error');
  const planName = searchParams.get('plan');

  useEffect(() => {
    // Log the failure for debugging
    if (status === 'failed' && subscriptionId) {
      console.log('Payment failed:', { subscriptionId, error, planName });
      
      // Optionally call API to log the failure
      //logPaymentFailure(subscriptionId, error);
    }
  }, [subscriptionId, status, error, planName]);

  const logPaymentFailure = async (subId: string, errorCode: string | null) => {
    try {
      // Replace with your actual API endpoint
      await fetch('/api/log-payment-failure', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          subscription_id: subId, 
          error_code: errorCode,
          timestamp: new Date().toISOString()
        }),
      });
    } catch (error) {
      console.error('Error logging payment failure:', error);
    }
  };

  const getErrorMessage = (errorCode: string | null) => {
    switch (errorCode) {
      case 'card_declined':
        return 'Your card was declined. Please try a different payment method.';
      case 'insufficient_funds':
        return 'Insufficient funds. Please check your account balance.';
      case 'expired_card':
        return 'Your card has expired. Please use a different card.';
      case 'processing_error':
        return 'There was an error processing your payment. Please try again.';
      case 'cancelled':
        return 'Payment was cancelled. You can try again anytime.';
      default:
        return 'Payment failed. Please try again or contact support if the issue persists.';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Failed
          </h1>
          <p className="text-gray-600">
            {getErrorMessage(error)}
          </p>
        </div>

        {subscriptionId && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-red-800 mb-1">Subscription Not Activated</h3>
            <p className="text-red-700 mb-2">
              Your subscription could not be activated due to payment failure.
            </p>
            <p className="text-sm text-red-600">
              Reference ID: {subscriptionId}
            </p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => navigate('/plans')}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2"
          >
            <CreditCard className="w-4 h-4" />
            <span>Try Again</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Back to Profile
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-3">
            Need help? Contact our support team:
          </p>
          <div className="space-y-2">
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              support@qixbi.com
            </button>
            <div className="text-sm text-gray-500">
              or call us at +1 (555) 123-4567
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Combined export for easier routing
export const PaymentResultPages = {
  Main: PaymentResult,
  Success: PaymentSuccess,
  Failure: PaymentFailure
};